import { useCallback, useMemo } from "react"

import { hasText } from "@/utils/string"
import { resolveUnitName, toPerUnitName } from "@/utils/unit"

import { useFormatCurrency } from "./use-format-currency"

const PLAIN_DECIMAL_PATTERN = /^-?\d+(?:\.\d+)?$/
const TWO_DECIMAL_RATE_UNITS = new Set(["day", "kw"])
const MAX_RATE_FRACTION_DIGITS = 5

const countFractionDigits = (value: string): number =>
  value.split(".")[1]?.length ?? 0

const resolveRateFractionDigits = (
  rate: string,
  unit: string | null
): number => {
  const trimmedRate = rate.trim()
  const trimmedUnit = unit?.trim().toLowerCase() ?? ""

  if (TWO_DECIMAL_RATE_UNITS.has(trimmedUnit)) {
    return 2
  }

  if (PLAIN_DECIMAL_PATTERN.test(trimmedRate)) {
    return Math.min(countFractionDigits(trimmedRate), MAX_RATE_FRACTION_DIGITS)
  }

  const parsed = Number.parseFloat(trimmedRate)
  if (!Number.isFinite(parsed)) {
    return 0
  }

  return countFractionDigits(String(parsed))
}

export type FormatChargeRate = (
  rate: string | null | undefined,
  unit: string | null | undefined,
  currency: string | null | undefined
) => string

/**
 * Formats a charge rate as currency at the tariff's own precision, then the
 * catalog per-unit. Kilowatt and day rates use two digits. Yen stays whole.
 * A negative rate keeps a leading minus.
 */
export const useFormatChargeRate = (): {
  formatChargeRate: FormatChargeRate
} => {
  const { formatCurrency } = useFormatCurrency(
    {},
    { useAccountingFormatWhenNegative: false }
  )

  const formatChargeRate = useCallback<FormatChargeRate>(
    (rate, unit, currency) => {
      const storedUnit = hasText(unit) ? unit.trim() : ""
      const perUnit = toPerUnitName(resolveUnitName(storedUnit))
      const formatted = formatCurrency(rate, {
        currency,
        ...(hasText(rate)
          ? { precision: resolveRateFractionDigits(rate, unit ?? null) }
          : {}),
        showPlaceholder: false,
        ...(perUnit != null ? { unit: perUnit } : {}),
      })

      if (formatted === "" || storedUnit === "" || perUnit != null) {
        return formatted
      }

      // The unit is not in the catalog, so append the stored text with the same spacing.
      return `${formatted} / ${storedUnit}`
    },
    [formatCurrency]
  )

  return useMemo(() => ({ formatChargeRate }), [formatChargeRate])
}
